import { Injectable, isDevMode } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Formio } from 'formiojs';
import axios from 'axios';
import { Token } from '../models/token';
import { TranslateService } from './translate.service';

/**
 * Centralizes Formio plugin registrations shared by all form-rendering components
 * (FormComponent, MyAccountPageComponent). Registered once as a root singleton.
 *
 * Plugins:
 * - **actionErrorHandler**: Intercepts Formio.makeRequest errors for action-bot
 *   requests and shows toastr notifications. These requests bypass Angular's
 *   HttpClient (and the ErrorInterceptor), and noAlerts: true suppresses
 *   Formio's built-in inline alerts.
 * - **custom-file-request**: Handles authorized file downloads (opens in new tab
 *   with Bearer token) and file delete operations.
 */
@Injectable({ providedIn: 'root' })
export class FormioPluginService {
  private registered = false;

  /** Caches file downloads by original URL → Promise<blobUrl>. */
  private downloadCache = new Map<string, Promise<string>>();

  constructor(
    private readonly toastr: ToastrService,
    private readonly translateService: TranslateService
  ) {}

  /**
   * Registers all Formio plugins. Idempotent — safe to call multiple times.
   */
  registerPlugins(): void {
    if (this.registered) {
      return;
    }
    this.registered = true;

    this.registerActionErrorPlugin();
    this.registerFilePlugin();

    // Sanitize [object Object] from Form.io content component interpolation.
    // Content templates like {{ _.get(data, "uploadField") }} render file
    // objects as [object Object] — strip them from the output.
    this.sanitizeInterpolation();
  }

  /**
   * Intercepts Formio.makeRequest rejections for action-type requests
   * (e.g., action-bot calls to smartlink URLs). Shows toastr with the
   * extracted error message, then re-throws so original handlers still work.
   */
  private registerActionErrorPlugin(): void {
    Formio.registerPlugin(
      {
        wrapRequestPromise: (promise: Promise<any>, requestArgs: any) => {
          return promise.catch((err: any) => {
            if (requestArgs?.type?.startsWith('action')) {
              const message =
                (typeof err === 'string' && err) ||
                err?.message ||
                err?.detail ||
                err?.error ||
                (err && typeof err === 'object'
                  ? Object.values(err)
                      .flat()
                      .filter((v: any) => typeof v === 'string')
                      .join(', ') || null
                  : null);
              try {
                this.toastr.error(
                  this.translateService.localizeId(
                    message || $localize`:@@action_failed:Action failed`
                  )
                );
              } catch {
                this.toastr.error(
                  message || $localize`:@@action_failed:Action failed`
                );
              }
            }
            throw err;
          });
        }
      },
      'actionErrorHandler'
    );
  }

  /**
   * Handles authorized file downloads (opens in new tab with Bearer token)
   * and file delete operations for url-stored files.
   */
  private registerFilePlugin(): void {
    Formio.registerPlugin(
      {
        wrapFileRequestPromise: (request: Promise<any>, requestArgs: any) => {
          if (requestArgs?.method === 'download') {
            return request.then((file: any) => this.handleFileDownload(file));
          }
          if (requestArgs?.method === 'upload') {
            return request.catch((error: any) => {
              throw String(this.extractFileUploadError(error));
            });
          }
          return request;
        },
        fileRequest: (requestArgs: any): Promise<boolean> | null => {
          if (
            requestArgs?.method === 'delete' &&
            requestArgs?.file?.storage === 'url'
          ) {
            return Promise.resolve(false);
          }
          return null;
        }
      },
      'custom-file-request'
    );
  }

  /**
   * Extracts a user-friendly error message from a file upload XHR rejection.
   * Formio's URL storage provider rejects with `xhr.response` (raw JSON string),
   * which the File component stores as `fileUpload.message`. Without parsing,
   * the template renders the raw JSON. This method parses DRF-style nested errors
   * (e.g. `{"file":["The submitted file is empty."]}`) into readable strings.
   */
  private extractFileUploadError(error: any): string {
    const fallback = $localize`:@@unable_to_upload_file:Unable to upload file`;

    if (error == null) {
      return fallback;
    }

    // XHR object or other non-string/non-object — use fallback
    if (typeof error !== 'string' && typeof error !== 'object') {
      return fallback;
    }

    // Already an object (unlikely from XHR but handle defensively)
    if (typeof error === 'object') {
      const strings = this.extractNestedStrings(error);
      if (strings.length > 0) {
        return this.translateService.localizeId(strings.join(', '));
      }
      return fallback;
    }

    // String — try to parse as JSON
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const strings = this.extractNestedStrings(parsed);
        if (strings.length > 0) {
          return this.translateService.localizeId(strings.join(', '));
        }
      }
      // Parsed but no strings extracted — return as-is
      return String(parsed);
    } catch {
      // Not JSON — return the raw string, translated if possible
      return this.translateService.localizeId(error);
    }
  }

  /**
   * Recursively extracts all string values from a nested object/array.
   * Handles DRF error formats like `{"field": ["error1", "error2"]}`.
   */
  private extractNestedStrings(obj: unknown): string[] {
    if (typeof obj === 'string') return [obj];
    if (Array.isArray(obj))
      return obj.flatMap((item) => this.extractNestedStrings(item));
    if (obj && typeof obj === 'object') {
      return Object.values(obj).flatMap((val) =>
        this.extractNestedStrings(val)
      );
    }
    return [];
  }

  /**
   * Downloads file with authorization header, returns file with blob URL.
   * No tabs are opened — Form.io handles tab opening via getFile() for user clicks,
   * and loadImage() uses the blob URL for inline img.src preview.
   */
  private async handleFileDownload(file: any): Promise<any> {
    if (!file?.url) {
      return file;
    }

    // Data URLs already contain the content — return as-is
    if (file.url.startsWith('data:')) {
      return file;
    }

    try {
      const blobUrl = await this.getOrDownload(file.url);
      return { ...file, url: blobUrl };
    } catch (error) {
      console.error('Error opening file:', error);
      return file;
    }
  }

  /**
   * Downloads a file with auth headers, caching the result. Concurrent calls
   * for the same URL share the same Promise (prevents race conditions with
   * linked components that trigger loadImage simultaneously).
   */
  private getOrDownload(url: string): Promise<string> {
    const cached = this.downloadCache.get(url);
    if (cached) {
      return cached;
    }

    // Evict oldest entry if cache exceeds limit to prevent unbounded blob memory
    if (this.downloadCache.size >= 20) {
      const oldest = this.downloadCache.keys().next().value!;
      this.evictCacheEntry(oldest);
    }

    const promise = axios
      .get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            Token.tokenNameInStorage
          )}`
        }
      })
      .then((resp) => URL.createObjectURL(resp.data));

    this.downloadCache.set(url, promise);
    promise.catch(() => this.downloadCache.delete(url));
    return promise;
  }

  private evictCacheEntry(url: string): void {
    const entry = this.downloadCache.get(url);
    if (entry) {
      entry.then((blobUrl) => URL.revokeObjectURL(blobUrl)).catch(() => {});
    }
    this.downloadCache.delete(url);
  }

  /**
   * Wraps Form.io's Evaluator.interpolate to strip [object Object] from
   * rendered content. Content component templates like
   * `{{ _.get(data, "uploadField") }}` render file objects as [object Object].
   */
  private sanitizeInterpolation(): void {
    // Patch utils.interpolate (the module export) — not Evaluator.interpolate.
    // Element.js caches a direct reference to the module export at import time,
    // so patching Evaluator.interpolate doesn't affect content component rendering.
    const utils = (Formio as any).Utils;
    if (!utils?.interpolate) {
      return;
    }
    const origInterpolate = utils.interpolate;
    const devMode = isDevMode();
    utils.interpolate = function (...args: any[]) {
      const result = origInterpolate.apply(this, args);
      if (typeof result === 'string' && result.includes('[object Object]')) {
        if (devMode) {
          console.warn(
            'FormioPluginService: stripped [object Object] from interpolation.',
            'Template:',
            args[0]
          );
        }
        return result.replace(/\[object Object\]/g, '');
      }
      return result;
    };
  }
}
